class AgeVerifierPopup extends HTMLElement {
  constructor() {
    super();
    this.cookieName = `Minimog:age-verifier-${this.id}`;
    // BL-10: a malformed cookie or blocked storage must never break the gate.
    try {
      this.cookie = getCookie(this.cookieName);
    } catch (e) {
      this.cookie = "";
    }

    this.modal = this.querySelector(".m-modal");
    this.modalContent = this.modal.querySelector(".m-modal--content");

    this.declineButton = this.querySelector("[data-age-verifier-decline-button]");
    this.declineContent = this.querySelector("[data-age-verifier-decline-content]");
    this.content = this.querySelector("[data-age-verifier-content]");

    this.agreeButton = this.querySelector("[data-age-verifier-agree-button]");
    this.returnButton = this.querySelector("[data-age-verifier-return-button]");

    if (this.cookie === "" && this.dataset.enable === "true") {
      // BL-10: never auto-open inside the theme editor.
      if (this.dataset.editor === "true" || (window.Shopify && window.Shopify.designMode)) return;
      this.openPopup();

      // Check session storage if user was editing on the second view
      const secondViewVisited = this.storageGet();
      if (!secondViewVisited) return;

      this.showDeclineContent();
    }
  }

  storageGet() {
    try {
      return sessionStorage.getItem(this.id);
    } catch (e) {
      return null;
    }
  }

  storageSet(value) {
    try {
      if (value) sessionStorage.setItem(this.id, value);
      else sessionStorage.removeItem(this.id);
    } catch (e) {
      // storage blocked: decline view simply is not remembered
    }
  }

  connectedCallback() {
    if (this.declineButton) {
      this.declineButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.showDeclineContent();
        this.storageSet("age-second-view");
      });
    }

    if (this.returnButton) {
      this.returnButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.hideDeclineContent();
        this.storageSet(null);
      });
    }

    if (this.agreeButton) {
      this.agreeButton.addEventListener("click", (e) => {
        e.preventDefault();
        this.hideDeclineContent(false);

        // Set cookie when design mode disabled
        if (this.dataset.enable === "true") {
          try {
            setCookie(this.cookieName, "agreed", 30);
          } catch (err) {
            // cookies blocked: gate closes for this page view only
          }
        }
        this.closePopup();
      });
    }

    if (Shopify.designMode) {
      document.addEventListener("shopify:section:select", this.__shopifySectionSelect.bind(this));
      document.addEventListener("shopify:section:load", this.__shopifySectionLoad.bind(this));
    }
  }

  disconnectedCallback() {
    if (Shopify.designMode) {
      document.removeEventListener("shopify:section:select", this.__shopifySectionSelect.bind(this));
      document.removeEventListener("shopify:section:load", this.__shopifySectionLoad.bind(this));
    }
  }

  __shopifySectionLoad(event) {
    if (event.detail.sectionId === this.dataset.sectionId && this.dataset.designMode === "true") {
      this.openPopup();

      // Check session storage if user was editing on the second view
      const secondViewVisited = this.storageGet();

      if (!secondViewVisited) return;

      this.showDeclineContent(event);
    }
  }

  __shopifySectionSelect(event) {
    if (event.detail.sectionId === this.dataset.sectionId && this.dataset.designMode === "true") {
      this.openPopup();
    } else {
      this.closePopup();
    }
  }

  showDeclineContent() {
    this.declineContent.classList.remove("m:hidden");
    this.content.classList.add("m:hidden");
    if (this.isOpen()) this.focusFirst(this.returnButton);
  }

  hideDeclineContent(moveFocus = true) {
    this.declineContent.classList.add("m:hidden");
    this.content.classList.remove("m:hidden");
    if (moveFocus && this.isOpen()) this.focusFirst(this.agreeButton);
  }

  // BL-10: keyboard support. Focus moves into the dialog on open and returns on close; no Tab trap.
  isOpen() {
    return this.modal.classList.contains("m-open-modal");
  }

  focusFirst(preferred) {
    if (window.Shopify && window.Shopify.designMode) return;
    const pane = this.content.classList.contains("m:hidden") ? this.declineContent : this.content;
    const target = preferred || pane.querySelector("button, [href], [tabindex]:not([tabindex='-1'])");
    if (target) target.focus({ preventScroll: true });
  }

  openPopup() {
    const wasOpen = this.isOpen();
    if (!wasOpen) this.lastFocused = document.activeElement;
    this.modal.removeAttribute("inert");
    this.modal.style.setProperty("--m-opacity", "1");
    this.modal.classList.add("m-open-modal");
    document.documentElement.classList.add("prevent-scroll");
    if (!wasOpen) this.focusFirst(this.agreeButton);
  }

  closePopup() {
    if (!this.isOpen()) return;
    const restore =
      this.lastFocused && this.lastFocused !== document.body && document.contains(this.lastFocused)
        ? this.lastFocused
        : document.getElementById("MainContent");
    if (restore && !(window.Shopify && window.Shopify.designMode)) {
      if (restore.id === "MainContent" && !restore.hasAttribute("tabindex")) restore.setAttribute("tabindex", "-1");
      restore.focus({ preventScroll: true });
    }
    this.modal.classList.remove("m-open-modal");
    this.modal.style.setProperty("--m-opacity", "0");
    this.modal.setAttribute("inert", "");
    document.documentElement.classList.remove("prevent-scroll");
    this.lastFocused = null;
  }
}

customElements.define("m-age-verifier-popup", AgeVerifierPopup);
